import type { BtlApplication } from 'api/btlApplicationForm/types';
import { ApplicationStatus } from 'api/btlApplicationForm/types';
import { resolveBtlCaseDisplayName } from 'api/btlApplicationForm/borrowerHelpers';
import {
  buildOoCaseSidebar,
  CaseSidebar,
  getVisibleOoCaseSidebarItemIds,
  mapOoCaseSidebarItemToUrlSection,
  resolveOoCaseSidebarItemId,
  shouldShowOoOverviewNavItem,
} from 'components/CaseSidebar';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { useBtlMissingFields } from 'queries/btlApplications/useBtlMissingFields';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

interface OoCaseSidebarProps {
  caseData?: BtlApplication;
  isCaseLoading?: boolean;
  currentSection?: string;
  context: OoCaseSidebarContext;
}

function getCaseSummaryTitle(caseData: BtlApplication): string {
  return resolveBtlCaseDisplayName(caseData);
}

function getOoCaseSidebarStatusLabel(
  status: ApplicationStatus | undefined,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string | undefined {
  if (!status) {
    return undefined;
  }

  if (status === ApplicationStatus.NEW) {
    return formatMessage({ id: 'caseSidebar.status.newApplication' });
  }

  return formatMessage({ id: `applicationStatus.${status}` });
}

export function OoCaseSidebar({ caseData, isCaseLoading, currentSection, context }: OoCaseSidebarProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isCreateMode = Boolean(context.isCreateMode);
  const { data: missingFieldsRes, refetch: refetchMissingFields } = useBtlMissingFields(
    isCreateMode ? '' : caseData?.id ?? '',
  );
  const activeItemId =
    resolveOoCaseSidebarItemId(currentSection, context) ??
    (isCreateMode ? CaseSidebarItemId.FINANCING : undefined) ??
    getVisibleOoCaseSidebarItemIds(context)[0] ??
    CaseSidebarItemId.OVERVIEW;

  useEffect(() => {
    if (isCreateMode) {
      return;
    }

    refetchMissingFields();
  }, [
    currentSection,
    caseData?.borrower?.individuals,
    caseData?.borrower?.companies,
    caseData?.applicationProperty?.status,
    refetchMissingFields,
    isCreateMode,
  ]);

  const handleItemSelect = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${mapOoCaseSidebarItemToUrlSection(itemId)}` });
  };

  const { overviewItem, state } = buildOoCaseSidebar({
    formatMessage,
    context,
    application: caseData,
    missingFields: missingFieldsRes?.missingFields,
    isMissingFieldsComplete: missingFieldsRes?.isComplete,
    householdsToHasIndividuals: missingFieldsRes?.householdsToHasIndividuals,
    isAffordabilityPassed: context.isAffordabilityPassed,
    isAffordabilityRunRequired: caseData?.isAffordabilityRunRequired,
  });

  return (
    <CaseSidebar
      overviewItem={overviewItem}
      state={state}
      activeItemId={activeItemId}
      onItemSelect={handleItemSelect}
      headerTitle={
        isCreateMode
          ? formatMessage({ id: 'btlApplicationForm.createCase.label' })
          : caseData
          ? getCaseSummaryTitle(caseData)
          : formatMessage({ id: 'btlApplicationForm.createCase.label' })
      }
      referenceNumber={isCreateMode ? undefined : caseData?.referenceNumber}
      statusLabel={
        isCreateMode
          ? formatMessage({ id: 'caseSidebar.status.newApplication' })
          : getOoCaseSidebarStatusLabel(caseData?.status, formatMessage)
      }
      application={caseData}
      isLoading={isCaseLoading}
      showOverviewItem={shouldShowOoOverviewNavItem(context)}
    />
  );
}
